import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import api, { formatApiError } from "@/lib/api";
import { isoLocalDate } from "@/lib/constants";
import Logo from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Clock, Check, CheckCircle2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { PrefFields } from "@/components/waitlist/PrefFields";

export default function Booking() {
    const { slug } = useParams();
    const { t } = useI18n();
    const [salon, setSalon] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [step, setStep] = useState(0);
    const [sel, setSel] = useState({ service: null, employee: "any", date: isoLocalDate(new Date()), time: null });
    const [slots, setSlots] = useState(null);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", email: "" });
    const [result, setResult] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [wl, setWl] = useState(null); // null | {step: 'prefs'|'details'|'done', f, result}
    const wlSet = (patch) => setWl((w) => ({ ...w, f: { ...w.f, ...patch } }));
    const startWaitlist = () => setWl({ step: "prefs", f: { service_ids: sel.service ? [sel.service.id] : [], employee_id: sel.employee !== "any" ? sel.employee : null, any_employee: sel.employee === "any", date_from: sel.date, date_to: null, preferred_days: [], time_from: null, time_to: null, flexible_date: true, flexible_time: false, flexible_employee: sel.employee === "any" } });
    const joinWaitlist = async () => {
        setSubmitting(true);
        try {
            const { data } = await api.post("/public/waitlist", { slug, ...wl.f, ...form, language: salon.language });
            setWl({ ...wl, step: "done", result: data.status });
        } catch (e) { toast.error(formatApiError(e)); } finally { setSubmitting(false); }
    };

    useEffect(() => {
        api.get(`/public/salon/${slug}`).then(({ data }) => setSalon(data)).catch(() => setNotFound(true));
    }, [slug]);

    useEffect(() => {
        if (step !== 2 || !sel.service) return;
        setLoadingSlots(true); setSlots(null);
        const params = { slug, service_id: sel.service.id, date: sel.date };
        if (sel.employee !== "any") params.employee_id = sel.employee;
        api.get("/public/availability", { params })
            .then(({ data }) => setSlots(data.slots))
            .catch(() => setSlots([]))
            .finally(() => setLoadingSlots(false));
    }, [step, sel.service, sel.employee, sel.date, slug]);

    if (notFound) return (
        <div className="flex min-h-screen items-center justify-center bg-secondary/30 px-6 text-center">
            <div><Logo /><p className="mt-6 text-muted-foreground">{t("booking.unavailable")}</p></div>
        </div>
    );
    if (!salon) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-accent" /></div>;

    const book = async () => {
        setSubmitting(true);
        try {
            const startIso = new Date(`${sel.date}T${sel.time}:00`).toISOString();
            let employee_id = sel.employee;
            if (employee_id === "any") {
                const slot = slots.find((s) => s.time === sel.time);
                employee_id = slot?.employee_ids[0];
            }
            const { data } = await api.post("/public/book", {
                slug, service_id: sel.service.id, employee_id, start: startIso,
                ...form, language: salon.language,
            });
            setResult(data);
            setStep(4);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setSubmitting(false); }
    };

    return (
        <div className="min-h-screen bg-secondary/30">
            <header className="border-b border-border/60 bg-background/80 backdrop-blur-xl">
                <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-4"><Logo /><LanguageSwitcher compact /></div>
            </header>
            <div className="mx-auto max-w-3xl px-6 py-10">
                <h1 className="font-serif text-3xl font-bold">{salon.name}</h1>
                <p className="mt-1 text-muted-foreground">{t("booking.title")}</p>

                {step < 4 && (
                    <div className="mt-8 rounded-3xl border border-border/70 bg-card p-6 md:p-8">
                        {step === 0 && (
                            <div>
                                <h2 className="mb-4 font-semibold">{t("booking.chooseService")}</h2>
                                <div className="grid gap-3">
                                    {salon.services.map((s) => (
                                        <button key={s.id} data-testid={`book-service-${s.id}`} onClick={() => { setSel({ ...sel, service: s }); setStep(1); }}
                                            className="flex items-center justify-between rounded-xl border border-border/70 p-4 text-left transition-colors duration-200 hover:border-accent hover:bg-accent/5">
                                            <div><p className="font-medium">{s.name}</p><p className="text-sm text-muted-foreground"><Clock className="mr-1 inline h-3.5 w-3.5" />{s.duration_min} {t("common.minutes")}</p></div>
                                            <span className="font-semibold">€{s.price.toFixed(2)}</span>
                                        </button>
                                    ))}
                                    {salon.services.length === 0 && <p className="text-sm text-muted-foreground">{t("services.noServices")}</p>}
                                </div>
                            </div>
                        )}
                        {step === 1 && (
                            <div>
                                <h2 className="mb-4 font-semibold">{t("booking.chooseEmployee")}</h2>
                                <div className="grid gap-3">
                                    <button onClick={() => { setSel({ ...sel, employee: "any" }); setStep(2); }} data-testid="book-emp-any"
                                        className="rounded-xl border border-border/70 p-4 text-left transition-colors duration-200 hover:border-accent hover:bg-accent/5">
                                        <p className="font-medium">{t("booking.anyEmployee")}</p>
                                    </button>
                                    {salon.employees.map((e) => (
                                        <button key={e.id} data-testid={`book-emp-${e.id}`} onClick={() => { setSel({ ...sel, employee: e.id }); setStep(2); }}
                                            className="flex items-center gap-3 rounded-xl border border-border/70 p-4 text-left transition-colors duration-200 hover:border-accent hover:bg-accent/5">
                                            <span className="h-8 w-1.5 rounded-full" style={{ background: e.color }} />
                                            <p className="font-medium">{e.name}</p>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {step === 2 && (
                            <div>
                                <h2 className="mb-4 font-semibold">{t("booking.chooseDate")} & {t("booking.chooseTime")}</h2>
                                <Input type="date" value={sel.date} min={isoLocalDate(new Date())} onChange={(e) => setSel({ ...sel, date: e.target.value, time: null })} data-testid="book-date" className="mb-4 max-w-xs" />
                                {loadingSlots ? <Loader2 className="h-6 w-6 animate-spin text-accent" /> : (
                                    (slots && slots.length > 0) ? (
                                        <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                                            {slots.map((s) => (
                                                <button key={s.time} data-testid={`book-slot-${s.time}`} onClick={() => { setSel({ ...sel, time: s.time }); setStep(3); }}
                                                    className="rounded-lg border border-border/70 py-2 text-sm font-medium transition-colors duration-200 hover:border-accent hover:bg-accent hover:text-accent-foreground">
                                                    {s.time}
                                                </button>
                                            ))}
                                        </div>
                                    ) : <p className="text-sm text-muted-foreground">{t("booking.noSlots")}</p>
                                )}
                                {!wl && (
                                    <div className="mt-6 rounded-xl border border-dashed border-border/70 p-4 text-sm" data-testid="wl-join-cta">
                                        <p className="font-medium">{t("wl.pub.noSlot")}</p>
                                        <Button variant="outline" onClick={startWaitlist} className="mt-2 rounded-full" data-testid="wl-join-btn">{t("wl.pub.join")}</Button>
                                    </div>
                                )}
                                {wl && wl.step === "prefs" && (
                                    <div className="mt-6 space-y-4 rounded-xl border border-accent/40 bg-accent/5 p-4" data-testid="wl-join-prefs">
                                        <p className="font-semibold">{t("wl.pub.join")}</p>
                                        <PrefFields f={wl.f} set={wlSet} employees={salon.employees} services={salon.services} t={t} />
                                        <Button onClick={() => setWl({ ...wl, step: "details" })} disabled={!wl.f.service_ids.length} className="w-full rounded-full" data-testid="wl-join-next">{t("common.next")}</Button>
                                    </div>
                                )}
                                {wl && wl.step === "details" && (
                                    <div className="mt-6 space-y-4 rounded-xl border border-accent/40 bg-accent/5 p-4" data-testid="wl-join-details">
                                        <div className="grid gap-3 sm:grid-cols-2">
                                            <div><Label>{t("customers.firstName")}</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="wl-firstname" className="mt-1.5" /></div>
                                            <div><Label>{t("customers.lastName")}</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="mt-1.5" /></div>
                                            <div><Label>{t("common.phone")}</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="wl-phone" className="mt-1.5" /></div>
                                            <div><Label>{t("common.email")}</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="wl-email" className="mt-1.5" /></div>
                                        </div>
                                        <Button onClick={joinWaitlist} disabled={submitting || !form.first_name || !form.email} className="w-full rounded-full" data-testid="wl-join-submit">{submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : t("wl.pub.joinBtn")}</Button>
                                    </div>
                                )}
                                {wl && wl.step === "done" && (
                                    <div className="mt-6 rounded-xl bg-accent/10 p-6 text-center" data-testid="wl-join-done">
                                        <CheckCircle2 className="mx-auto h-10 w-10 text-accent" />
                                        <p className="mt-3 font-semibold">{wl.result === "already_listed" ? t("wl.pub.already") : t("wl.pub.listed")}</p>
                                        <p className="mt-1 text-sm text-muted-foreground">{t("wl.pub.listedSub")}</p>
                                    </div>
                                )}
                            </div>
                        )}
                        {step === 3 && (
                            <div>
                                <h2 className="mb-4 font-semibold">{t("booking.yourDetails")}</h2>
                                <div className="mb-4 rounded-xl bg-secondary/50 p-4 text-sm">
                                    <p className="font-medium">{sel.service.name} · {sel.date} · {sel.time}</p>
                                </div>
                                <div className="grid gap-4 sm:grid-cols-2">
                                    <div><Label>{t("customers.firstName")}</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="book-firstname" className="mt-1.5" /></div>
                                    <div><Label>{t("customers.lastName")}</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="book-lastname" className="mt-1.5" /></div>
                                    <div><Label>{t("common.email")}</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="book-email" className="mt-1.5" /></div>
                                    <div><Label>{t("common.phone")}</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="book-phone" className="mt-1.5" /></div>
                                </div>
                                <Button onClick={book} disabled={submitting || !form.first_name || !form.email} className="mt-6 w-full rounded-full" data-testid="book-confirm">
                                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : t("booking.confirmBooking")}
                                </Button>
                            </div>
                        )}
                        {step > 0 && step < 4 && (
                            <button onClick={() => setStep(step - 1)} className="mt-6 inline-flex items-center text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="mr-1 h-4 w-4" />{t("common.back")}</button>
                        )}
                    </div>
                )}

                {step === 4 && result && (
                    <div className="mt-8 rounded-3xl border border-border/70 bg-card p-10 text-center" data-testid="book-success">
                        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-accent/12 text-accent"><CheckCircle2 className="h-9 w-9" /></div>
                        <h2 className="mt-4 font-serif text-2xl font-bold">{t("booking.booked")}</h2>
                        <p className="mt-2 text-muted-foreground">{t("booking.bookedSub")}</p>
                        <p className="mt-4 text-sm">{t("booking.reference")}: <span className="font-mono font-semibold">{result.reference}</span></p>
                        <Button variant="outline" onClick={() => { setStep(0); setResult(null); setSel({ ...sel, time: null }); setForm({ first_name: "", last_name: "", phone: "", email: "" }); }} className="mt-6 rounded-full">{t("booking.bookAnother")}</Button>
                    </div>
                )}
            </div>
        </div>
    );
}
